/* eslint-disable max-statements */
class BreadcrumbManager {
  private breadcrumbs: {
    type: 'navigation' | 'click' | 'custom';
    data: any;
    timestamp: string;
  }[] = [];
  private maxSize = 15;

  add(type: 'navigation' | 'click' | 'custom', data: any) {
    this.breadcrumbs.push({
      type,
      data,
      timestamp: new Date().toISOString(),
    });
    if (this.breadcrumbs.length > this.maxSize) {
      this.breadcrumbs.shift();
    }
  }

  get() {
    return [...this.breadcrumbs];
  }

  clear() {
    this.breadcrumbs = [];
  }
}

const breadcrumbs = new BreadcrumbManager();

if (typeof window !== 'undefined') {
  let lastPathname = window.location.pathname;

  const trackNavigation = () => {
    const currentPathname = window.location.pathname;
    if (currentPathname !== lastPathname) {
      breadcrumbs.add('navigation', {
        from: lastPathname,
        to: currentPathname,
      });
      lastPathname = currentPathname;
    }
  };

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    originalPushState.apply(window.history, args);
    trackNavigation();
  };

  window.history.replaceState = function (...args) {
    originalReplaceState.apply(window.history, args);
    trackNavigation();
  };

  window.addEventListener('popstate', trackNavigation);

  const getElementIdentifier = (element: HTMLElement): string | null => {
    const trackAttr = element.getAttribute('data-track');
    if (trackAttr) return trackAttr;

    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    if (element.tagName === 'BUTTON') {
      const text = element.textContent?.trim();
      if (text && text.length < 50) return `Button: ${text}`;
    }

    if (element.tagName === 'A') {
      const href = (element as HTMLAnchorElement).pathname;
      const text = element.textContent?.trim();
      if (text && text.length < 50) return `Link: ${text} (${href})`;
      return `Link: ${href}`;
    }

    if (element.tagName === 'INPUT' || element.tagName === 'SELECT') {
      const name = element.getAttribute('name');
      const placeholder = element.getAttribute('placeholder');
      return name || placeholder || element.tagName;
    }

    return null;
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as HTMLElement;

      const interactiveElement = target.closest('button, a, input, select, [role="button"]') as HTMLElement;

      if (interactiveElement) {
        const identifier = getElementIdentifier(interactiveElement);
        if (identifier) {
          breadcrumbs.add('click', {
            element: identifier,
            path: window.location.pathname,
          });
        }
      }
    },
    true,
  );
}

const sentErrors = new Map<string, number>();
const THROTTLE_MS = 1000;

const sendToLoggingProxy = async (
  payload: { severity: 'ERROR' | 'WARNING' | 'INFO'; message: string; stack?: string; metadata?: Record<string, any> },
  loggingProxyUrl: string,
) => {
  if (!loggingProxyUrl) return;

  const errorKey = `${payload.severity}:${payload.message}`;
  const now = Date.now();
  const lastSent = sentErrors.get(errorKey);

  if (lastSent && now - lastSent < THROTTLE_MS) {
    return;
  }

  sentErrors.set(errorKey, now);

  for (const [key, timestamp] of sentErrors.entries()) {
    if (now - timestamp > THROTTLE_MS) {
      sentErrors.delete(key);
    }
  }

  try {
    await fetch(loggingProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        breadcrumbs: breadcrumbs.get(),
        path: window.location.pathname,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (err) {
    // Silently fail
  }
};

export const useFrontendLogger = () => {
  return {
    sendToLoggingProxy,
    breadcrumbs,
  };
};
